import type { IntlKey } from 'locales';
import type { BlockMetadata, FormBlockMetadataById } from 'models/metadata/block.definition';
import { createSelector } from 'reselect';
import { getProductDescription } from 'state/forms/metadata/selectors';
import { collectDescendantNodeIds } from 'state/product-description/helper';
import type { ProductGraph, ProductNode } from 'state/product-description/model';
import type { AppState } from 'state/state.model';
import type { QuoteWorkspaceVersion } from 'state/userPreferences/userPreferences.model';
import { getUserPreferencesQuoteWorkspaceVersion } from 'state/userPreferences/userPreferences.selectors';
import type { WorkspaceType } from 'state/workspaces/model';

import { getBlocksMetadata } from '../../forms/metadata/selectors';
import { getProductVariation } from './misc';
import { getStaticFormInfos } from './staticForm';
import { getUserDefaultProductView } from './user';

export type ModifiedTabCandidate = Readonly<{
  labelKey: IntlKey;
  fieldIds: readonly string[];
}>;

const EMPTY_CANDIDATES: readonly ModifiedTabCandidate[] = [];

function isTabVisible(
  metadata: BlockMetadata,
  quoteWorkspaceVersion: QuoteWorkspaceVersion,
  mustForceAdvancedMode: boolean,
  hiddenTabsIds: readonly string[] = [],
): boolean {
  return !(
    metadata.isHidden ||
    hiddenTabsIds.includes(metadata.id) ||
    (quoteWorkspaceVersion === 'Standard' && metadata.isForAdvancedMode && !mustForceAdvancedMode)
  );
}

function getFormTabsContainer(productDescription: ProductGraph | undefined, blocksMetadata: FormBlockMetadataById) {
  const isTabsNode = (node: ProductNode) => blocksMetadata[node.id]?.type === 'Tabs';
  return (
    productDescription?.find(node => node.id === 'TabContainer' && isTabsNode(node)) ??
    productDescription?.find(isTabsNode)
  );
}

function getOrderedTabNodes(
  tabContainer: ProductNode | undefined,
  visibleTabsId: string[] | undefined,
  hiddenTabsId: string[] | undefined,
): readonly ProductNode[] {
  const children = tabContainer?.children ?? [];

  if (!visibleTabsId?.length && !hiddenTabsId?.length) {
    return children;
  }

  const orderedTabIds = [...(visibleTabsId ?? []), ...(hiddenTabsId ?? [])];
  return orderedTabIds
    .map(tabId => children.find(child => child.id === tabId))
    .filter((node): node is ProductNode => !!node);
}

function getSidePanelProductDescription(
  state: AppState,
  workspaceType: WorkspaceType,
  tabId: string,
): ProductGraph | undefined {
  if (workspaceType === 'dynamic') {
    return getProductDescription(state, tabId);
  }

  const infos = getStaticFormInfos(state, tabId);
  if (!infos?.instrumentName || !infos.productDescription) {
    return undefined;
  }

  return getProductVariation(state, getUserDefaultProductView(state), {
    productType: infos.instrumentName,
    productSubType: infos.productDescription,
  });
}

export const getModifiedTabCandidates = createSelector(
  [
    (_state: AppState, workspaceType: WorkspaceType, _tabId: string) => workspaceType,
    (state: AppState, workspaceType: WorkspaceType, tabId: string) =>
      workspaceType === 'static' || workspaceType === 'dynamic'
        ? getBlocksMetadata(state, workspaceType, tabId)
        : undefined,
    (state: AppState, workspaceType: WorkspaceType, tabId: string) =>
      getSidePanelProductDescription(state, workspaceType, tabId),
    (state: AppState, workspaceType: WorkspaceType, _tabId: string) =>
      workspaceType === 'static' ? (state.userPreferences.tabsOrder ?? null) : null,
    (state: AppState, workspaceType: WorkspaceType, _tabId: string) =>
      workspaceType === 'static' ? getUserPreferencesQuoteWorkspaceVersion(state) : 'Advanced',
    (state: AppState, workspaceType: WorkspaceType, tabId: string) =>
      workspaceType === 'static' ? (getStaticFormInfos(state, tabId)?.mustForceAdvancedMode ?? false) : true,
  ],
  (
    workspaceType,
    blocksMetadata,
    productDescription,
    tabsOrder,
    quoteWorkspaceVersion,
    mustForceAdvancedMode,
  ): readonly ModifiedTabCandidate[] => {
    if ((workspaceType !== 'static' && workspaceType !== 'dynamic') || !blocksMetadata) {
      return EMPTY_CANDIDATES;
    }

    const tabContainer = getFormTabsContainer(productDescription, blocksMetadata);
    const visibleTabsId = tabsOrder?.spmVisible;
    const hiddenTabsId = tabsOrder?.spmHidden;

    return getOrderedTabNodes(tabContainer, visibleTabsId, hiddenTabsId)
      .filter(tabNode => {
        const metadata = blocksMetadata[tabNode.id];
        return metadata && isTabVisible(metadata, quoteWorkspaceVersion, mustForceAdvancedMode, hiddenTabsId);
      })
      .map(tabNode => ({
        labelKey: `form.${tabNode.id}` as IntlKey,
        fieldIds: collectDescendantNodeIds(tabNode),
      }));
  },
);
